/** Tuning options of the {@link SpeechSegmentDetector} */
export interface SpeechSegmentDetectorOptions {
  /** Level above the background noise floor treated as speech, in dB */
  speechAboveNoiseDb: number
  /** Absolute minimum level of speech, in dBFS (ignores level changes in near silence) */
  minSpeechDb: number
  /** Minimum amount of speech in a segment worth committing, in ms (ignores clicks and short noises) */
  minSpeechMs: number
  /** Silence after speech that ends the segment, in ms */
  endSilenceMs: number
  /** Duration after which a shorter pause ends the segment, in ms (e.g. fast turns of meeting participants) */
  longSegmentMs: number
  /** Silence that ends a long segment, in ms: any pause between words */
  longSegmentEndSilenceMs: number
  /** Max segment duration, in ms: commits continuous speech (or speech-like noise) anyway */
  maxSegmentMs: number
  /** How fast the noise floor follows rising background noise, in dB per second */
  noiseFloorRiseDbPerSecond: number
  /** Time constant of the level smoothing used for the noise floor, in ms (short dips like dropouts don't count) */
  levelSmoothingMs: number
}

/** Distance the noise floor keeps from the speech threshold while speech is detected, in dB */
const SPEECH_FLOOR_MARGIN_DB = 3

/**
 * Defaults tuned by evaluating OpenAI transcription of noisy and telephone audio:
 * shorter silences split sentences at phrase pauses, higher thresholds miss speech in heavy noise
 */
const defaultOptions: SpeechSegmentDetectorOptions = {
  speechAboveNoiseDb: 6,
  minSpeechDb: -55,
  minSpeechMs: 300,
  endSilenceMs: 1500,
  longSegmentMs: 30000,
  longSegmentEndSilenceMs: 300,
  maxSegmentMs: 60000,
  noiseFloorRiseDbPerSecond: 0.5,
  levelSmoothingMs: 300,
}

/**
 * Simple energy-based voice activity detector deciding when a speech segment should be committed for transcription.
 * Feed it the audio level of consecutive audio frames: it tracks the background noise floor (following the smoothed
 * level down immediately, but rising slowly and staying below sustained speech), treats frames clearly above it
 * as speech, and reports the end of a segment after a silence following speech. Long segments (e.g. fast turns
 * in a meeting) end at any shorter pause, and segments without pauses are cut at the max duration.
 */
export class SpeechSegmentDetector {
  private readonly options: SpeechSegmentDetectorOptions
  /** Smoothed audio power (linear) */
  private smoothedPower: number | null = null
  /** Estimated background noise level, in dBFS */
  private noiseFloorDb: number | null = null
  /** Time since the current segment started, in ms (null if no speech since the last commit) */
  private segmentMs: number | null = null
  /** Amount of speech in the current segment, in ms */
  private speechMs = 0
  /** Silence since the last speech frame, in ms */
  private silenceMs = 0
  /** Amount of audio above the absolute speech level since the last reset, in ms (speech or not) */
  private soundMs = 0

  constructor(options: Partial<SpeechSegmentDetectorOptions> = {}) {
    this.options = { ...defaultOptions, ...options }
  }

  /**
   * Process the next audio frame.
   * @param levelDb RMS level of the frame, in dBFS
   * @param frameMs duration of the frame, in ms
   * @returns true if the current speech segment has ended and should be committed
   */
  process(levelDb: number, frameMs: number): boolean {
    const { options } = this

    // Smooth the level in the power domain, so short dips (e.g. dropouts) don't count as background noise
    const power = 10 ** (levelDb / 10)
    const smoothing = frameMs / (options.levelSmoothingMs + frameMs)
    this.smoothedPower =
      this.smoothedPower === null ? power : this.smoothedPower + smoothing * (power - this.smoothedPower)
    // Levels below the speech threshold margin are all quiet enough (e.g. digital silence of muted audio)
    const smoothedDb = Math.max(10 * Math.log10(this.smoothedPower), options.minSpeechDb - options.speechAboveNoiseDb)

    // Track the noise floor: follow the smoothed level down immediately
    if (this.noiseFloorDb === null || smoothedDb < this.noiseFloorDb) this.noiseFloorDb = smoothedDb

    const isSpeech = levelDb > Math.max(this.noiseFloorDb + options.speechAboveNoiseDb, options.minSpeechDb)

    // Rise slowly with the background noise. During speech, stay clearly below the speech threshold,
    // so sustained speech can't lift the floor to its own level and suppress itself,
    // while speech-like background noise (e.g. other voices in the room) can still raise it
    const risenFloorDb = this.noiseFloorDb + (options.noiseFloorRiseDbPerSecond * frameMs) / 1000
    const maxFloorDb = isSpeech ? smoothedDb - options.speechAboveNoiseDb - SPEECH_FLOOR_MARGIN_DB : smoothedDb
    this.noiseFloorDb = Math.max(this.noiseFloorDb, Math.min(risenFloorDb, maxFloorDb))
    if (levelDb > options.minSpeechDb) this.soundMs += frameMs

    // Speech starts or continues the segment
    if (isSpeech) {
      this.segmentMs ??= 0
      this.speechMs += frameMs
      this.silenceMs = 0
    } else {
      this.silenceMs += frameMs
    }
    if (this.segmentMs === null) return false
    this.segmentMs += frameMs

    // The segment ends after a silence (a shorter one once the segment is long), or when it gets too long
    const endSilenceMs =
      this.segmentMs >= options.longSegmentMs ? options.longSegmentEndSilenceMs : options.endSilenceMs
    const isSilenceAfterSpeech = this.silenceMs >= endSilenceMs
    if (!isSilenceAfterSpeech && this.segmentMs < options.maxSegmentMs) return false

    // Only commit segments with enough speech, short noises are left for the next segment
    const shouldCommit = this.speechMs >= options.minSpeechMs
    if (shouldCommit || isSilenceAfterSpeech) this.reset()
    return shouldCommit
  }

  /** Whether the current segment has enough speech to be committed */
  get hasPendingSpeech() {
    return this.speechMs >= this.options.minSpeechMs
  }

  /**
   * Whether enough sound was heard since the last reset, even if not recognized as speech
   * (e.g. speech from the very first frame, which then counts as the background noise level)
   */
  get hasPendingSound() {
    return this.soundMs >= this.options.minSpeechMs
  }

  /** Start a new segment, e.g. after a manual commit */
  reset() {
    this.segmentMs = null
    this.speechMs = 0
    this.silenceMs = 0
    this.soundMs = 0
  }
}

/**
 * Compute the RMS level of PCM samples (floats in the -1..1 range), in dBFS
 */
export function getRmsLevelDb(samples: ArrayLike<number>): number {
  let sumOfSquares = 0
  for (let i = 0; i < samples.length; i++) sumOfSquares += samples[i] * samples[i]
  const rms = Math.sqrt(sumOfSquares / Math.max(samples.length, 1))
  return 20 * Math.log10(Math.max(rms, 1e-9))
}
